import React, { useEffect, useRef } from 'react';
import { Baby, ShieldCheck, Sparkles, Clock } from 'lucide-react';

/**
 * KidCareBanner — high-converting, animated callout for the on-site Kid Care
 * add-on. Placed between Memberships and Reviews so parents see it the moment
 * they're weighing a plan.
 *
 * Design notes:
 *   • Heavy 3D parallax — mouse-follow on the headline and trust badges
 *   • Cinematic red glow halos that scale on hover
 *   • Floating "playful" geometric shapes (squares + dots) to subconsciously
 *     evoke a kids' play space without being literal
 *   • CTA points back to #memberships so it pushes plan selection
 *   • No "free" wording anywhere
 */
export default function KidCareBanner() {
  const wrapRef = useRef(null);

  // Subtle mouse-tracking parallax (matches the rest of the site's ambient FX)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 2; // -1 → 1
      const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      el.style.setProperty('--mx', `${x * 8}px`);
      el.style.setProperty('--my', `${y * 8}px`);
    };
    const leave = () => {
      el.style.setProperty('--mx', '0px');
      el.style.setProperty('--my', '0px');
    };
    el.addEventListener('mousemove', handler);
    el.addEventListener('mouseleave', leave);
    return () => {
      el.removeEventListener('mousemove', handler);
      el.removeEventListener('mouseleave', leave);
    };
  }, []);

  const trustItems = [
    { Icon: ShieldCheck, label: 'TRUSTED ATTENDANTS', sub: 'Trained caregivers, every shift' },
    // === KID CARE HOURS ===
    // Replaces the previous 'FLEXIBLE HOURS · Drop-off that fits your workout'
    // badge sub-copy with the exact approved times. Rendered inline as JSX so
    // the three time ranges stack on their own lines without introducing any
    // new component / DOM structure — the surrounding badge card (icon + label
    // + sub) is unchanged. Sunday is intentionally omitted per the approved
    // spec. Non-breaking hyphens on the time dashes keep each range on one
    // line down to 320 px.
    {
      Icon: Clock,
      label: 'HOURS',
      sub: (
        <>
          <div>Mon&ndash;Fri: 8:00&nbsp;AM&nbsp;&ndash;&nbsp;12:00&nbsp;PM</div>
          <div>Mon&ndash;Fri: 5:00&nbsp;PM&nbsp;&ndash;&nbsp;8:30&nbsp;PM</div>
          <div>Sat: 8:00&nbsp;AM&nbsp;&ndash;&nbsp;12:00&nbsp;PM</div>
        </>
      ),
    },
    { Icon: Sparkles,    label: 'ADD-ON TO ANY PLAN',  sub: 'Starts at $15/mo for 1 child' },
  ];

  return (
    <section
      id="kid-care"
      data-testid="kid-care-banner"
      ref={wrapRef}
      className="relative pt-2 pb-12 sm:pb-24 lg:pb-32 bg-black overflow-hidden"
      style={{ '--mx': '0px', '--my': '0px' }}
    >
      {/* === Ambient red glow halos === */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-10 left-1/4 w-[55%] h-[55%] bg-red-700/15 blur-[140px] rounded-full"
          style={{ transform: 'translate3d(var(--mx), var(--my), 0)' }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[40%] h-[40%] bg-red-600/12 blur-[120px] rounded-full"
          style={{ transform: 'translate3d(calc(var(--mx) * -1), calc(var(--my) * -1), 0)' }}
        />
      </div>

      {/* === Floating playful shapes — subtle, decorative === */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <span className="kid-float absolute top-[18%] left-[8%]  w-3 h-3 rounded-sm bg-red-600/40 [animation-delay:0s]" />
        <span className="kid-float absolute top-[28%] right-[12%] w-2 h-2 rounded-full bg-red-500/55 [animation-delay:.4s]" />
        <span className="kid-float absolute bottom-[20%] left-[14%] w-4 h-4 rounded-md bg-red-700/40 rotate-12 [animation-delay:.8s]" />
        <span className="kid-float absolute bottom-[30%] right-[10%] w-2.5 h-2.5 rounded-full bg-red-400/40 [animation-delay:1.2s]" />
        <span className="kid-float absolute top-[42%] left-[48%] w-1.5 h-1.5 rounded-full bg-red-300/60 [animation-delay:.6s]" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          {/* === LEFT: Copy + CTA === */}
          <div className="relative reveal">
            {/* Eyebrow with pulsing red dot */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-red-600/45 shadow-[0_4px_18px_rgba(239,43,43,0.3)] mb-6"
              data-testid="kid-care-eyebrow"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-[10px] tracking-[0.35em] text-white font-bold">ON-SITE KID CARE</span>
            </div>

            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
              style={{ transform: 'translate3d(calc(var(--mx) * 0.3), calc(var(--my) * 0.3), 0)' }}
            >
              BRING <span className="text-red-500">THE KIDS</span>
              <br />
              <span className="kid-shine">TRAIN WITHOUT GUILT.</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-[480px]">
              Drop the kids at our on-site <span className="text-white font-semibold">Kid Care</span> while you crush a workout. It's available as a simple
              <span className="text-red-400 font-semibold"> add-on to any membership</span> — so a missed sitter never costs you a session again.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-red-700/50 hover:border-red-500 hover:bg-red-700/15 text-white text-xs tracking-[0.3em] font-bold transition"
              >
                ASK A QUESTION
              </a>
            </div>
          </div>

          {/* === RIGHT: 3D card stack with trust badges === */}
          <div className="relative reveal-right">
            {/* Hero card — big baby silhouette icon + red glow */}
            <div
              className="relative rounded-3xl border border-red-700/50 bg-gradient-to-br from-red-950/60 via-black to-black p-8 sm:p-10 shadow-[0_30px_80px_rgba(239,43,43,0.35)]"
              style={{ transform: 'translate3d(calc(var(--mx) * -0.6), calc(var(--my) * -0.6), 0)' }}
            >
              <div className="absolute inset-0 rounded-3xl pointer-events-none opacity-30" style={{
                background: 'radial-gradient(circle at 30% 20%, rgba(239,43,43,0.6) 0%, transparent 55%)',
              }} />

              <div className="relative flex items-center justify-between mb-8">
                <div>
                  <div className="text-[10px] tracking-[0.4em] text-red-400 font-bold mb-2">PARENTS' SECRET WEAPON</div>
                  <div className="font-display text-2xl sm:text-3xl">Drop-In Kid Care</div>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-red-500/40 blur-2xl rounded-full" />
                  <div className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-red-600/95 shadow-[0_10px_30px_rgba(239,43,43,0.5)] kid-bounce">
                    <Baby className="w-9 h-9 sm:w-11 sm:h-11 text-white" strokeWidth={1.6} />
                  </div>
                </div>
              </div>

              {/* Trust strip */}
              <div className="relative space-y-3" data-testid="kid-care-trust">
                {trustItems.map(({ Icon, label, sub }, i) => (
                  <div
                    key={label}
                    className="flex items-center gap-4 p-3 rounded-xl bg-black/55 border border-red-900/40 hover:border-red-500/60 transition group"
                    style={{ transform: `translateY(${i * 0}px)` }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-600/20 border border-red-500/40 flex items-center justify-center group-hover:bg-red-600/35 transition">
                      <Icon className="w-5 h-5 text-red-400" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.3em] text-white font-bold">{label}</div>
                      <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing footnote */}
              <div className="relative mt-7 pt-5 border-t border-red-900/40 flex items-end justify-between">
                <div>
                  <div className="text-[9px] tracking-[0.4em] text-zinc-500 font-bold">STARTING AT</div>
                  <div className="font-display text-3xl text-white">
                    $15<span className="text-base text-zinc-400"> / mo</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] tracking-[0.3em] text-red-400 font-bold">PRICED BY</div>
                  <div className="text-[11px] tracking-[0.2em] text-white">NUMBER OF KIDS</div>
                </div>
              </div>
            </div>

            {/* Decorative back card to add depth */}
            <div className="absolute -bottom-6 -right-6 w-2/3 h-3/4 rounded-3xl border border-red-700/30 bg-red-950/20 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
